import { getCountries, getCountryCallingCode, parsePhoneNumber, isValidNumber } from "libphonenumber-js";

// validate mobile number fields and return an updated errors object
const validateMobile = (values, errors, id, optional) => {
    const countryCodeId = `${id}CountryCode`;
    const prefixId = `${id}Prefix`;
    const numberId = `${id}Number`;
    const tempErrors = errors;

    if (!optional || (values[prefixId] || values[numberId])) {
        if (!values[countryCodeId]) {
            tempErrors[countryCodeId] = "Country is a required field";
        }

        if (!values[prefixId]) {
            tempErrors[prefixId] = "Prefix is a required field";
        }

        if (!values[numberId]) {
            tempErrors[numberId] = "Mobile is a required field";
        }
    }

    if (values[countryCodeId] && values[prefixId] && values[numberId]) {
        // get the country code e.g. IE-353 -> 353
        const countryCode = values[countryCodeId].substr(values[countryCodeId].indexOf("-") + 1, values[countryCodeId].length);
        const fullPhoneLength = countryCode.length + values[prefixId].length + values[numberId].length;

        if (fullPhoneLength > 15) {
            tempErrors[numberId] = "Full phone number cannot be longer than 15 digits long.";
        }

        if (!isValidNumber(values[id])) {
            tempErrors[numberId] = "Please enter a valid phone number.";
        }
    }

    if (values[prefixId]) {
        // Ireland number should be 7 digits long
        if (values[countryCodeId] === "IE-353") {
            if (!/08[0-9]/g.test(values[prefixId]) && !/8[0-9]/g.test(values[prefixId])) {
                tempErrors[prefixId] = "Prefix should be in the format 08X";
            }
        } else if (values[countryCodeId] === "GB-44") {
            if (!/07[0-9]/g.test(values[prefixId]) && !/7[0-9]/g.test(values[prefixId])) {
                tempErrors[prefixId] = "Prefix should be in the format 07X";
            }
        }
    }

    if (values[numberId]) {
        // Ireland number should be 7 digits long
        if (values[countryCodeId] === "IE-353") {
            if (String(values[numberId]).length !== 7) {
                tempErrors[numberId] = "Mobile number should be 7 digits long for an Irish mobile number";
            }
        } else if (values[countryCodeId] === "GB-44") {
            if (String(values[numberId]).length !== 8) {
                tempErrors[numberId] = "Mobile number should be 8 digits long for a UK mobile number";
            }
        } else if (String(values[numberId]).length > 10) {
            // Mobile number is max of 10 OR Max of 15 when combining country code, prefix and mobile number
            tempErrors[numberId] = "Mobile number should be less than 10 digits.";
        }
    }

    return tempErrors;
};

// get a list of countries to populate a cuntry code select
const getCountryData = () => {
    const countries = getCountries();
    const countriesList = [];

    // Inbuilt JS for converting a countryCoode to a name
    const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

    // define variables for loop
    let prefix;
    let countryName;
    let countryCode;
    let formattedObj;
    let irelandObj;
    let UKObj;

    // loop through countries list
    for (let i = 0; i < countries.length; i += 1) {
        // use library to get the calling code e.g. IE -> 353
        prefix = getCountryCallingCode(countries[i]);
        countryName = regionNames.of(countries[i]);
        countryCode = countries[i];

        // format each object for the select
        formattedObj = { value: `${countryCode}-${prefix}`, id: `${countryCode}-${prefix}`, name: `${countryName} +${prefix}`, available: true };

        // order the list IE -> UK -> ROW
        if (countryCode === "IE") {
            irelandObj = formattedObj;
        } else if (countryCode === "GB") {
            UKObj = formattedObj;
        } else {
            countriesList.push(formattedObj);
        }
    }

    return [irelandObj, UKObj, ...countriesList];
};

/**
 *
 * @param {string} countryCode
 * @param {string} prefix
 * returns field restrictions
 */
const setMobileFieldRestrictions = (countryCode, prefix) => {
    if (countryCode) {
        if (countryCode === "IE-353") {
            return {
                mobileMaxLength: "7",
                // if the first digit of the prefix is 8 set the max to 2 otherwise 3
                prefixMaxLength: prefix && prefix.charAt(0) === "8" ? "2" : "3"
            };
        }
        if (countryCode === "GB-44") {
            return {
                mobileMaxLength: "8",
                // if the first digit of the prefix is 7 set the max to 2 otherwise 3
                prefixMaxLength: prefix && prefix.charAt(0) === "7" ? "2" : "3"
            };
        }
    }
    return {
        mobileMaxLength: "10",
        prefixMaxLength: "5"
    };
};

/**
 *
 * @param {string} country
 * @param {string} nationalNumber
 * return the prefix for the country
 */
const getprefixFromCountry = (country, nationalNumber) => {
    // get first two digits for the prefix
    if (country === "IE" || country === "GB") {
        return {
            prefix: nationalNumber.substr(0, 2),
            restOfNumber: nationalNumber.substr(2, nationalNumber.length)
        };
    }
    return {
        prefix: nationalNumber.substr(0, 5),
        restOfNumber: nationalNumber.substr(5, nationalNumber.length)
    };
};

/**
 *
 * @param {string} number
 * return the broken down values for the phone numbers
 */
const getPhoneNumberValues = number => {
    if (number) {
        const phoneNumberData = parsePhoneNumber(number);
        const prefixInfo = getprefixFromCountry(phoneNumberData.country, phoneNumberData.nationalNumber);

        return {
            fullNumber: number,
            countryCode: `${phoneNumberData.country}-${phoneNumberData.countryCallingCode}`,
            prefix: prefixInfo.prefix,
            subNumber: prefixInfo.restOfNumber
        };
    }
    return {
        fullNumber: "",
        countryCode: "IE-353",
        prefix: "",
        subNumber: ""
    };
};

export { validateMobile, getCountryData, getPhoneNumberValues, setMobileFieldRestrictions };
